import { z } from "zod"
import { getToken } from "./session"

const BASE_URL = import.meta.env.VITE_API_URL ?? ""

// Seis falhas, porque o RH faz coisas diferentes em cada uma: buscar o e-mail novo, tentar de
// novo daqui a pouco, ou nos avisar. Um erro só, genérico, empurra todo mundo para o mesmo lugar
// errado — e quem recebe "tente mais tarde" para um link vencido vai tentar mais tarde para sempre.
export type FalhaDoPortal =
    | "link" // token ausente, vencido ou recusado — só um e-mail novo resolve
    | "rede" // o navegador não chegou até nós — internet, proxy, VPN da empresa
    | "vazio" // 404: não há o que mostrar para este CNPJ — empresa sem escrituração no mês
    | "servidor" // chegou e voltou erro — problema nosso
    | "resposta" // voltou 200 com corpo que não bate com o schema — também problema nosso
    | "recusado" // uma regra do serviço recusou (400, 409, 422); a mensagem dele pode ir ao RH

export class ApiError extends Error {
    constructor(
        readonly falha: FalhaDoPortal,
        readonly status = 0,
        readonly mensagem?: string,
    ) {
        super(falha)
        this.name = "ApiError"
    }
}

const RecusaSchema = z.object({ message: z.string() })

// Toda resposta passa pelo schema antes de virar estado. O portal mostra dinheiro e faz afirmação
// sobre a folha de terceiros: é melhor falhar na borda, com erro nomeado, do que renderizar meio
// dado e deixar o RH concluir errado.
const chamar = async <T>(path: string, schema: z.ZodType<T>, init: RequestInit): Promise<T> => {
    let response: Response
    try {
        response = await fetch(`${BASE_URL}${path}`, {
            ...init,
            headers: {
                ...(typeof init.body === "string" ? { "Content-Type": "application/json" } : {}),
                ...init.headers,
            },
        })
    } catch {
        // fetch só rejeita quando a requisição não completou. Qualquer resposta, inclusive 500,
        // chega no caminho de baixo.
        throw new ApiError("rede")
    }

    if (response.status === 401 || response.status === 403) {
        throw new ApiError("link", response.status)
    }

    // O serviço responde 404 tanto para "não existe" quanto para "não é seu", de propósito. Para
    // quem está com o link certo, os dois querem dizer o mesmo: não há nada aqui para esta empresa.
    if (response.status === 404) {
        throw new ApiError("vazio", response.status)
    }

    if ([400, 409, 422].includes(response.status)) {
        const recusa = RecusaSchema.safeParse(await response.json().catch(() => null))
        throw new ApiError("recusado", response.status, recusa.data?.message)
    }

    if (!response.ok) {
        throw new ApiError("servidor", response.status)
    }

    try {
        return schema.parse(response.status === 204 ? undefined : await response.json())
    } catch {
        throw new ApiError("resposta", response.status)
    }
}

export const request = async <T>(
    path: string,
    schema: z.ZodType<T>,
    init: RequestInit = {},
): Promise<T> => {
    const token = getToken()
    if (!token) throw new ApiError("link")
    return chamar(path, schema, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, ...init.headers },
    })
}

// As rotas /open não usam a sessão do portal: quem autoriza é o token que vai no próprio pedido —
// o do descadastro, por exemplo, que não dá acesso a nenhuma tela da empresa.
export const requestAberto = <T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}) =>
    chamar(path, schema, init)
